<template>
  <section v-if="education?.length" class="education pe-section">
    <h3>
      Education
      <SectionActions
        v-show="isPowerEdit"
        class="actions"
        section="education"
      />
    </h3>
    <template v-for="(exp, index) in cOrderedEducation" :key="exp.institution">
      <section v-if="exp.institution || exp.studyType" class="experience">
        <ItemActions
          v-show="isPowerEdit"
          class="itemActions"
          :index="index"
          section="education"
          :uuid="exp.uuid"
          :actions="['edit', 'delete']"
        />
        <section class="title">
          <a :href="getNewTabUrl(exp.url)" target="_blank">
            <div
              @click.prevent
              v-editable="isPowerEdit"
              :data-target-path="`resume.education.${index}.institution`"
              class="institution"
              v-text="exp.institution"
            ></div>
          </a>
          <div class="dates" v-if="exp.startDate || exp.endDate !== undefined">
            <div>
              <DateDisplay
                v-if="shouldDisplayStartDate"
                :date="exp.startDate"
                :updatePath="`resume.education.${index}.startDate`"
                :isEditable="isPowerEdit"
                :isInvalid="!checkDates(exp.startDate, exp.endDate)"
                @mouseenter="
                  $renderTooltip(
                    $event,
                    'Invalid date range',
                    !checkDates(exp.startDate, exp.endDate)
                  )
                "
              />
              <span v-if="exp.startDate && shouldDisplayStartDate"> - </span>
              <DateDisplay
                :date="exp.endDate"
                hasCurrentOption
                :updatePath="`resume.education.${index}.endDate`"
                :isEditable="isPowerEdit"
                :isInvalid="!checkDates(exp.startDate, exp.endDate)"
                @mouseenter="
                  $renderTooltip(
                    $event,
                    'Invalid date range',
                    !checkDates(exp.startDate, exp.endDate)
                  )
                "
              />
            </div>
          </div>
        </section>
        <div class="details">
          <span
            v-editable="isPowerEdit"
            :data-target-path="`resume.education.${index}.studyType`"
            class="studyType"
            v-text="exp.studyType"
          ></span>
          <span
            class="area"
            v-editable="isPowerEdit"
            :data-target-path="`resume.education.${index}.area`"
            v-text="exp.area"
          ></span>
        </div>
        <div class="score" v-if="exp.minors && exp.minors.length">
          Minor:
          <EducationMinor
            v-for="(minor, minorIndex) in exp.minors"
            :key="minorIndex"
            :minor="minor"
            :index="minorIndex"
            :section="`education.${index}.minors`"
            :isEditable="isPowerEdit"
            @remove="minorIndex => removeMinor(index, minorIndex)"
            @add="minorIndex => addMinor(index, minorIndex)"
          />
        </div>
        <div class="score" v-if="exp.courses && exp.courses.length">
          Relevant Coursework:
          <EducationCoursework
            v-for="(course, courseIndex) in exp.courses"
            :key="courseIndex"
            :course="course"
            :index="courseIndex"
            :section="`education.${index}.courses`"
            :isEditable="isPowerEdit"
            @remove="courseIndex => removeCourse(index, courseIndex)"
            @add="courseIndex => addCourse(index, courseIndex)"
          />
        </div>
        <span class="score" v-if="exp.score">
          GPA:
          <span
            v-editable="isPowerEdit"
            :data-target-path="`resume.education.${index}.score`"
            v-text="exp.score"
          ></span>
        </span>
      </section>
    </template>
  </section>
  <section class="print:hidden" v-else-if="isPowerEdit">
    <button class="addSectionButton" @click="addEducationItem">
      <FontAwesomeIcon :icon="['fas', 'plus']" class="icon" />
      <span>Add education</span>
    </button>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, nextTick } from 'vue'
import { Resume } from '@/types/resume/v1'
import { useStructuredResumeStore } from '@/store/pinia/structuredResume'
import {
  GlobalEvents,
  useGlobalListeners,
} from '@/composables/useGlobalListeners'
import SectionActions from './SectionActions.vue'
import ItemActions from './ItemActions.vue'
import EducationMinor from './EducationMinor.vue'
import EducationCoursework from './EducationCoursework.vue'
import DateDisplay from './support/DateDisplay.vue'
import sortItems from '@/lib/dates/dateRangeSorter'
import { checkDates } from '@/lib/dates/dateHelper'
import { getNewTabUrl } from '@/lib/urls'

export default defineComponent({
  name: 'Education',
  components: {
    SectionActions,
    DateDisplay,
    ItemActions,
    EducationMinor,
    EducationCoursework,
  },
  props: {
    education: {
      type: Object as PropType<Resume.Education[]>,
      required: false,
    },
  },
  setup(props) {
    const store = useStructuredResumeStore()
    const { publishEvent } = useGlobalListeners()

    const cOrderedEducation = computed(() => {
      return sortItems(props.education || [])
    })

    const shouldDisplayStartDate = computed(() => {
      return store.resume?.meta?.template !== 'kubusug'
    })

    const addEducationItem = async () => {
      const uuid = store.addSectionItem('education')
      await nextTick()
      publishEvent(GlobalEvents.PE_EDIT_SECTION, {
        section: 'education',
        uuid,
      })
    }

    const removeCourse = (educationIndex, index) => {
      store.removeEducationCourse(educationIndex, index)
    }

    const removeMinor = (educationIndex, index) => {
      store.removeEducationMinor(educationIndex, index)
    }

    const addCourse = (educationIndex, index) => {
      store.addEducationCourse(educationIndex, index)
    }

    const addMinor = (educationIndex, index) => {
      store.addEducationMinor(educationIndex, index)
    }

    return {
      isPowerEdit: store.isPowerEdit,
      cOrderedEducation,
      shouldDisplayStartDate,
      getNewTabUrl,
      addEducationItem,
      checkDates,
      removeCourse,
      removeMinor,
      addCourse,
      addMinor,
    }
  },
})
</script>

<style lang="postcss" scoped>
.education {
  @apply text-base-em leading-[inherit] pt-6-em block @container;
  .score {
    @apply text-sm-em mt-2-em;
  }
  .title {
    @apply flex flex-col @sm:flex-row justify-between items-start mb-2-em;
  }
  a {
    @apply text-inherit cursor-text;
  }
  h3 {
    @apply leading-none text-xl-em flex items-center;
    .actions {
      @apply hidden;
    }
    &:hover {
      .actions {
        @apply flex;
      }
    }
  }
  .experience {
    @apply pt-4-em leading-[inherit] relative;
    .itemActions {
      @apply hidden;
    }
    &:hover {
      .itemActions {
        @apply flex;
      }
    }
    .institution {
      @apply font-semibold;
    }
    .details {
      @apply flex flex-row text-sm-em flex-wrap;
      .studyType {
        @apply font-semibold mr-2-em;
      }
    }
    .dates {
      @apply text-sm-em;
    }
  }
}
</style>
